import JsonServiceClient from 'bz-stores/lib/JsonServiceClient';
import AuthStore from 'bz-stores/lib/auth-store';
import { observable, runInAction } from 'mobx';
import type { CredentialCreateOptions, CredentialMakeResult } from 'tm3-core/api.dtos';
import {
  GetAssertionOptions,
  MakeAssertionRequest,
  MakeCredentialsOptionsRequest,
  RegisterCredentialWithServerRequest
} from 'tm3-core/api.dtos';
import { coerceToArrayBuffer, coerceToBase64Url } from './fido-utils';

class FidoStore {
  @observable loading = false;
  @observable message = '';
  error?: Error = undefined;
  client = new JsonServiceClient();

  async onFidoLogin(): Promise<boolean> {
    const res = await this.client.post(new GetAssertionOptions());
    console.log('Assertion Options Object', res);

    if (res.status !== 'ok') {
      runInAction(() => {
        this.message = res.errorMessage ?? 'Unable to begin pass key login.';
      });
      return false;
    }
    (res.challenge as any) = coerceToArrayBuffer(res.challenge);

    res.allowCredentials.forEach(function (listItem) {
      (listItem.id as any) = coerceToArrayBuffer(listItem.id);
    });

    let credential;
    try {
      credential = await navigator.credentials.get({ publicKey: res });
    } catch (err: any) {
      runInAction(() => {
        this.message = err.message ? err.message : err;
        this.error = err;
      });
      return false;
    }

    if (!credential) {
      runInAction(() => {
        this.message = 'No pass key could be found';
      });
      return false;
    }

    try {
      await this.verifyFidoLoginResponse(credential);
      return true;
    } catch (e: any) {
      runInAction(() => {
        this.message = 'The pass key could not be verified';
        this.error = e;
      });
      return false;
    }
  }

  async verifyFidoLoginResponse(cred: any) {
    let authData = new Uint8Array(cred.response.authenticatorData);
    let clientDataJSON = new Uint8Array(cred.response.clientDataJSON);
    let rawId = new Uint8Array(cred.rawId);
    let sig = new Uint8Array(cred.response.signature);
    let userHandle = new Uint8Array(cred.response.userHandle);

    try {
      const { result } = await this.client.post(
        new MakeAssertionRequest({
          id: cred.id,
          rawId: coerceToBase64Url(rawId),
          type: cred.type,
          extensions: cred.getClientExtensionResults(),
          response: {
            authenticatorData: coerceToBase64Url(authData),
            clientDataJson: coerceToBase64Url(clientDataJSON),
            userHandle: userHandle !== null ? coerceToBase64Url(userHandle) : null,
            signature: coerceToBase64Url(sig)
          }
        })
      );
      // show error
      if (!result) {
        runInAction(() => {
          this.message = 'There was a problem verifying your pass key.';
        });
        return false;
      }

      return result;
    } catch (e: any) {
      runInAction(() => {
        this.message = 'There was a problem verifying your pass key.';
        this.error = e;
      });
      return false;
    }
  }

  async onFidoRegister() {
    if (!AuthStore.stage1Authenticated || !AuthStore.user) return;
    // send to server for registering
    let credOptions: CredentialCreateOptions | null = null;
    try {
      const res = await this.client.post(
        new MakeCredentialsOptionsRequest({
          displayName: AuthStore.user.email,
          attType: 'none',
          authType: '',
          userVerification: 'preferred',
          residentKey: 'required'
        })
      );
      credOptions = res;
    } catch (e: any) {
      runInAction(() => {
        this.message = 'There was a problem registering your credentials';
        this.error = e;
      });
      return false;
    }

    console.log('Credential Options Object', credOptions);

    if (!credOptions == null || credOptions!.status !== 'ok') {
      runInAction(() => {
        this.message = credOptions!.errorMessage;
      });
      return false;
    }

    // Turn the challenge back into the accepted format of padded base64
    (credOptions!.challenge as any) = coerceToArrayBuffer(credOptions!.challenge);
    // Turn ID into a UInt8Array Buffer for some reason
    (credOptions!.user.id as any) = coerceToArrayBuffer(credOptions!.user.id);

    credOptions!.excludeCredentials = credOptions!.excludeCredentials.map((c) => {
      (c.id as any) = coerceToArrayBuffer(c.id);
      return c;
      ``;
    });

    let newCredential;
    try {
      newCredential = await navigator.credentials.create({
        publicKey: credOptions as PublicKeyCredentialCreationOptions
      });
    } catch (e: any) {
      runInAction(() => {
        if (e.toString().includes('already registered')) {
          this.message =
            'Your device is already registered with a Passkey, please go to the sign in screen and sign in.';
          this.error = undefined;
        } else {
          this.message = 'Could not create credentials in browser.';
          this.error = e;
        }
      });
      return false;
    }

    try {
      if (newCredential.response) return this.registerNewCredential(newCredential);
      else {
        this.message = 'There was a problem registering your pass key';
      }
    } catch (e: any) {
      runInAction(() => {
        this.message = 'There was a problem registering your pass key.';
        this.error = e;
      });
      return false;
    }
  }

  async registerNewCredential(newCredential: any) {
    let attestationObject = new Uint8Array(newCredential.response.attestationObject);
    let clientDataJSON = new Uint8Array(newCredential.response.clientDataJSON);
    let rawId = new Uint8Array(newCredential.rawId);

    let response: CredentialMakeResult | undefined = undefined;
    try {
      const res = await this.client.post(
        new RegisterCredentialWithServerRequest({
          id: newCredential.id,
          rawId: coerceToBase64Url(rawId),
          type: newCredential.type,
          extensions: newCredential.getClientExtensionResults(),
          response: {
            attestationObject: coerceToBase64Url(attestationObject),
            clientDataJson: coerceToBase64Url(clientDataJSON)
          }
        })
      );
      response = res.result;
    } catch (e: any) {
      runInAction(() => {
        this.message = 'There was an error registering your Pass Key';
        this.error = e;
      });
      return false;
    }

    if (!response || response!.status !== 'ok') {
      runInAction(() => (this.message = response?.errorMessage ?? 'No Response'));
      return false;
    }

    return true;
  }
}
export { FidoStore };
